'use client';

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Send, Paperclip, Smile } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ConnectionStatus } from '@/store/chatStore';

interface AnimatedMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  status: ConnectionStatus;
  disabled?: boolean;
  onFileSelect?: (file: File) => void;
}

const EMOJIS = [
  '😀','😂','🥰','😍','😎','🤔','😅','😊',
  '😭','😢','😤','😠','🥺','😋','🤩','🥳',
  '👍','👎','👋','🙌','🤝','❤️','💔','🔥',
  '💯','✨','🎉','🎊','😈','💀','🤦','🙈',
  '😴','🤯','🥴','😷','💩','👀','🫡','😬',
];

function useAutoResizeTextarea(minHeight: number, maxHeight: number) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const adjust = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = `${minHeight}px`;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [minHeight, maxHeight]);

  const reset = useCallback(() => {
    if (ref.current) ref.current.style.height = `${minHeight}px`;
  }, [minHeight]);

  return { ref, adjust, reset };
}

export function AnimatedMessageInput({
  value,
  onChange,
  onSend,
  onKeyDown,
  status,
  disabled,
  onFileSelect,
}: AnimatedMessageInputProps) {
  const isConnected = status === 'connected';
  const [focused, setFocused] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const { ref: textareaRef, adjust, reset } = useAutoResizeTextarea(44, 120);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value === '') reset();
    else adjust();
  }, [value, adjust, reset]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiOpen) return;
    const handler = (e: MouseEvent) => {
      if (emojiPanelRef.current && !emojiPanelRef.current.contains(e.target as Node)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emojiOpen]);

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const newValue = value.slice(0, start) + emoji + value.slice(end);
    onChange(newValue);
    // Restore cursor after emoji
    setTimeout(() => {
      el.selectionStart = start + emoji.length;
      el.selectionEnd = start + emoji.length;
      el.focus();
    }, 0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onFileSelect) return;
    onFileSelect(file);
    e.target.value = '';
  };

  const placeholder =
    status === 'searching'
      ? 'Finding a stranger\u2026'
      : status === 'connected'
      ? 'Message\u2026'
      : 'Connect to start chatting';

  const canSend = isConnected && value.trim().length > 0 && !disabled;

  return (
    <div className="border-t border-white/[0.06] bg-brand-surface/90 backdrop-blur-sm p-3 relative" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>

      {/* Emoji picker panel */}
      <AnimatePresence>
        {emojiOpen && (
          <motion.div
            ref={emojiPanelRef}
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-3 mb-2 z-20 bg-brand-surface border border-white/[0.08] rounded-2xl shadow-2xl p-3 w-[264px]"
          >
            <div className="grid grid-cols-8 gap-0.5">
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-white/[0.08] text-lg leading-none transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
        className="hidden"
        onChange={handleFileChange}
      />

      <motion.div
        className="relative rounded-2xl overflow-hidden"
        animate={
          focused && isConnected
            ? { boxShadow: '0 0 0 2px rgba(196,214,0,0.25)' }
            : { boxShadow: '0 0 0 1px rgba(255,255,255,0.06)' }
        }
        transition={{ duration: 0.2 }}
      >
        <div
          className={cn(
            'flex items-end gap-2 bg-brand-surface-2 rounded-2xl px-3 py-2 transition-opacity duration-200',
            (!isConnected || disabled) && 'opacity-60'
          )}
        >
          {/* Emoji button */}
          <button
            type="button"
            disabled={!isConnected || !!disabled}
            onClick={() => isConnected && !disabled && setEmojiOpen((p) => !p)}
            className={cn(
              'shrink-0 self-end mb-1 p-1 transition-colors disabled:cursor-not-allowed',
              emojiOpen ? 'text-brand-lime' : 'text-brand-gray hover:text-brand-gray-light'
            )}
            aria-label="Emoji picker"
          >
            <Smile size={17} />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => { onChange(e.target.value); adjust(); }}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            disabled={!isConnected || !!disabled}
            rows={1}
            maxLength={2000}
            aria-label="Chat message"
            className={cn(
              'flex-1 bg-transparent resize-none text-sm text-brand-white placeholder:text-brand-gray',
              'py-2 focus:outline-none leading-relaxed disabled:cursor-not-allowed'
            )}
            style={{ minHeight: '44px', maxHeight: '120px', overflow: 'hidden' }}
          />

          {/* Attachment button */}
          <motion.button
            type="button"
            whileTap={{ scale: 0.92 }}
            disabled={!isConnected || !!disabled}
            onClick={() => isConnected && !disabled && fileInputRef.current?.click()}
            className="shrink-0 self-end mb-1 p-1 text-brand-gray hover:text-brand-gray-light transition-colors disabled:cursor-not-allowed"
            aria-label="Send file"
          >
            <Paperclip size={16} />
          </motion.button>

          {/* Send button */}
          <motion.button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            whileHover={canSend ? { scale: 1.05 } : {}}
            whileTap={canSend ? { scale: 0.93 } : {}}
            aria-label="Send message"
            className={cn(
              'shrink-0 self-end mb-0.5 flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200',
              canSend
                ? 'bg-brand-lime text-brand-black shadow-lg shadow-brand-lime/20'
                : 'bg-brand-surface text-brand-gray-muted border border-white/[0.06] cursor-not-allowed'
            )}
          >
            <Send size={15} />
          </motion.button>
        </div>

        {/* Animated focus ring */}
        <AnimatePresence>
          {focused && isConnected && (
            <motion.span
              className="absolute inset-0 rounded-2xl pointer-events-none ring-2 ring-brand-lime/25 ring-offset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Character count */}
      <AnimatePresence>
        {value.length > 1800 && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="text-right text-[10px] text-brand-gray mt-1 pr-1"
          >
            {value.length}/2000
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
